import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AuraCastPlaylistPage() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const minutes = Math.max(1, parseInt(params.get('minutes') || '5', 10));
  const interval = parseInt(params.get('interval') || '10', 10) * 1000;
  const [auras, setAuras] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [media, setMedia] = useState([]);
  const [mediaIdx, setMediaIdx] = useState(0);
  const [err, setErr] = useState('');
  const wakeLockRef = useRef(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/orgs/${orgId}/dashboard`)
      .then(r => {
        const list = (r.data?.auras || []).filter(a => !!a.aura_key);
        setAuras(list);
        if (!list.length) setErr('No events available for playlist.');
      })
      .catch(() => setErr('Failed to load org events.'));
  }, [orgId]);

  // Rotate auras every N minutes
  useEffect(() => {
    if (auras.length < 2) return;
    const t = setTimeout(() => setActiveIdx(i => (i + 1) % auras.length), minutes * 60 * 1000);
    return () => clearTimeout(t);
  }, [auras, activeIdx, minutes]);

  // Fetch media for active aura
  const fetchMedia = useCallback(async () => {
    const a = auras[activeIdx];
    if (!a) return;
    try {
      const res = await axios.get(`${API_URL}/api/auracast/${a.aura_key}`, { withCredentials: true });
      const list = (res.data?.media || []).filter(m => m.file_type === 'image' || m.file_type === 'video');
      list.sort(() => Math.random() - 0.5);
      setMedia(list);
      setMediaIdx(0);
    } catch (_) { setMedia([]); }
  }, [auras, activeIdx]);

  useEffect(() => { fetchMedia(); }, [fetchMedia]);
  // Signed file links expire: re-read the list every 30 minutes.
  useEffect(() => {
    const t = setInterval(fetchMedia, 30 * 60 * 1000);
    return () => clearInterval(t);
  }, [fetchMedia]);

  useEffect(() => {
    if (!media.length) return;
    const t = setTimeout(() => setMediaIdx(i => (i + 1) % media.length), interval);
    return () => clearTimeout(t);
  }, [media, mediaIdx, interval]);

  // Wake lock
  useEffect(() => {
    const req = async () => {
      try { if ('wakeLock' in navigator) wakeLockRef.current = await navigator.wakeLock.request('screen'); }
      catch (_) {}
    };
    req();
    const onVis = () => { if (document.visibilityState === 'visible') req(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const current = media[mediaIdx];
  const aura = auras[activeIdx];

  return (
    <div className="min-h-screen w-screen bg-black flex items-center justify-center overflow-hidden">
      <button
        onClick={() => navigate('/org/dashboard')}
        className="absolute top-4 left-4 z-50 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg text-sm"
        aria-label="Exit playlist"
      >
        ← Exit
      </button>
      {aura && (
        <div className="absolute top-4 right-4 z-50 bg-white/10 text-white px-4 py-2 rounded-lg text-sm">
          {aura.title} · {activeIdx + 1}/{auras.length}
        </div>
      )}
      {err && <div className="text-white/70 text-2xl font-serif">{err}</div>}
      {!err && !current && (
        <div className="text-white/60 text-2xl font-serif">Loading playlist…</div>
      )}
      {current && current.file_type === 'image' && (
        <img key={current.id} src={`${API_URL}${current.url}`} alt={current.caption || 'Photo'}
             className="max-h-screen max-w-screen object-contain animate-fade-in" />
      )}
      {current && current.file_type === 'video' && (
        <video key={current.id} src={`${API_URL}${current.url}`} autoPlay muted playsInline
               className="max-h-screen max-w-screen animate-fade-in" />
      )}
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        .animate-fade-in { animation: fadeIn 1s ease }
      `}</style>
    </div>
  );
}
